import React from 'react'
import Accordion from '../Accordion/Accordion'
import PropTypes from 'prop-types'
import * as Styled from './NursingIndiactor.styles'
import { Indicator, IndicatorLegend, IndicatorOverlayContent } from './partials'
import { TextContext } from 'hocs'
import { Button } from 'components/atoms'
import { Overlay } from 'components/molecules'

const NursingIndicator = ({
  intro,
  info,
  comment,
  reportDate,
  type,
  result,
  variant = 'circle',
}) => {
  const textContext = React.useContext(TextContext)
  const [selectedIndicator, setSelectedIndicator] = React.useState({})
  const [open, setOpen] = React.useState(false)
  const [indicators, setIndicators] = React.useState([])

  React.useEffect(() => {
    const _indicators = new Map()
    result.forEach((indicator) => {
      const enumeration = indicator.criteria.charAt(0)

      const existingEnumeration = _indicators.get(enumeration)
      const indicatorWithTitle = {
        ...indicator,
        title:
          textContext[
            `nursingIndicator.indicator.title.${ variant }.${ indicator.criteria }`
          ],
      }

      if (existingEnumeration) {
        _indicators.set(enumeration, [
          ...existingEnumeration,
          indicatorWithTitle,
        ])
      } else {
        _indicators.set(enumeration, [indicatorWithTitle])
      }
    })
    setIndicators(_indicators)
  }, [result, textContext, variant])

  const handleMoreDetailsClick = (indicator) => {
    setSelectedIndicator(indicator)
    setOpen(true)
  }

  const checkForSpecialCriteria = (key) => {
    if (variant === 'square' && key === '5') {
      return false
    }
    return true
  }

  // do not render if no results present
  if (result.length === 0) {
    return null
  }

  return (
    <Styled.NursingIndicator>
      <p>
        <b>{textContext[`nursingIndicator.title.${ variant }`]}</b>
      </p>

      {intro && <p>{intro}</p>}

      {info && <p>{info}</p>}

      {reportDate && (
        <p>
          {textContext['nursingIndicator.ratingDate.title']}
          <b>{reportDate.from}</b>
        </p>
      )}

      {type && (
        <p>
          {textContext['nursingIndicator.auditType.title']}
          <b>{type}</b>
        </p>
      )}

      <div className='indicator-legend-accordion'>
        <Accordion label='Bedeutung der Symbole' type='UNDERLINED'>
          <IndicatorLegend variant={ variant } />
        </Accordion>
      </div>
      {variant === 'circle' && (
        <>
          <p>
            <b>{textContext['nursingIndicator.singleCase']}</b>
          </p>
          <p>{textContext['nursingIndicator.singleCase.explanation']}</p>
        </>
      )}
      <p>
        <b>{textContext[`nursingIndicator.table.title.${ variant }`]}</b>
      </p>
      <p>{textContext[`nursingIndicator.rating.explanation.${ variant }`]}</p>
      <hr />
      {[...indicators].map(([key, indicator]) => (
        <React.Fragment key={ key }>
          <div className='indicator-wrapper'>
            <b className='indicator-enumeration'>{key}.</b>
            <div className='indicator-title'>
              <b className='indicator-type-description'>
                {
                  textContext[
                    `nursingIndicator.indicator.title.${ variant }.${ key }.`
                  ]
                }
              </b>
              {indicator && indicator.length === 1 && (
                <div className='indicator-rating'>
                  <Indicator variant={ variant } rating={ indicator[0].total } />
                </div>
              )}
              {indicator &&
                (indicator.length === 1 || !checkForSpecialCriteria(key)) && (
                  <Button
                    className='btn-link more-details-button'
                    text='Mehr Details'
                    onClick={ () =>
                      handleMoreDetailsClick({
                        title: `${ key }. ${
                          textContext[
                            `nursingIndicator.indicator.title.${ variant }.${ key }.`
                          ]
                        }`,
                        indicator,
                      })
                    }
                  />
                )}
            </div>
          </div>
          {indicator && indicator.length > 1 && checkForSpecialCriteria(key) && (
            <div className='indicator-types'>
              {indicator.map((type, typeIndex) => (
                <div key={ `type-${ typeIndex }` } className='indicator-wrapper'>
                  <b className='indicator-type-char'>{type.criteria}</b>
                  <div className='indicator-type'>
                    <p dangerouslySetInnerHTML={{ __html: type.title }} />
                    <div className='indicator-rating'>
                      <Indicator variant={ variant } rating={ type.total } />
                    </div>
                    <Button
                      className='btn-link more-details-button'
                      text='Mehr Details'
                      onClick={ () =>
                        handleMoreDetailsClick({
                          title: `${ key }. ${
                            textContext[
                              `nursingIndicator.indicator.title.${ variant }.${ key }.`
                            ]
                          }`,
                          type,
                        })
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          <hr />
        </React.Fragment>
      ))}

      {comment && (
        <>
          <p>
            <b>{textContext['nursingIndicator.comment.title']}</b>
          </p>
          <p>{comment}</p>
        </>
      )}

      <Overlay
        id={ 'indicator-overlay ' }
        isActive={ open }
        title={ textContext[`nursingIndicator.overlay.title.${ variant }`] }
        showClose
        onToggleActive={ () => setOpen(false) }>
        <IndicatorOverlayContent
          indicator={ selectedIndicator }
          variant={ variant }
        />
      </Overlay>
    </Styled.NursingIndicator>
  )
}

NursingIndicator.propTypes = {
  intro: PropTypes.string,
  info: PropTypes.string,
  comment: PropTypes.string,
  reportDate: PropTypes.shape({
    from: PropTypes.string,
    until: PropTypes.string,
  }),
  type: PropTypes.string,
  result: PropTypes.arrayOf(
    PropTypes.shape({
      criteria: PropTypes.string,
      desc: PropTypes.string,
      total: PropTypes.number,
      lastResult: PropTypes.arrayOf(
        PropTypes.shape({
          reportDate: PropTypes.string,
          result: PropTypes.number,
        })
      ),
      subcriteria: PropTypes.shape({
        total: PropTypes.number,
        result: PropTypes.arrayOf(
          PropTypes.shape({
            criteria: PropTypes.string,
            count: PropTypes.number,
          })
        ),
      }),
    })
  ),
  variant: PropTypes.oneOf(['circle', 'square']),
}

export default NursingIndicator
